// src/pages/Admin/DepensesRecettes/DepensesRecettesCharts.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const fmtEUR = (v) =>
  (Number(v || 0)).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });

const MONTHS_FR = [
  "janv.", "févr.", "mars", "avr.", "mai", "juin",
  "juil.", "août", "sept.", "oct.", "nov.", "déc.",
];
// Ordre saison sportive : Juil -> Juin
const BUDGET_ORDER = [6, 7, 8, 9, 10, 11, 0, 1, 2, 3, 4, 5];

function useIsDark() {
  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false
  );
  useEffect(() => {
    if (typeof MutationObserver === "undefined") return;
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains("dark"))
    );
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

export default function DepensesRecettesCharts({ rows, saison }) {
  const isDark = useIsDark();

  // Agrégats mensuels (barres Dépense/Recette + ligne Solde cumulée)
  const monthly = useMemo(() => {
    // base des 12 mois
    const base = Array.from({ length: 12 }, (_, m) => ({
      monthIdx: m,
      month: MONTHS_FR[m],
      depense: 0,
      recette: 0,
    }));

    for (const r of rows) {
      if (!r?.date_operation) continue;
      const d = new Date(r.date_operation);
      if (isNaN(d.getTime())) continue;
      const m = d.getMonth(); // 0..11
      const idx = base.findIndex((x) => x.monthIdx === m);
      if (idx >= 0) {
        if (r.type_operation === "Dépense") base[idx].depense += Number(r.montant || 0);
        else if (r.type_operation === "Recette") base[idx].recette += Number(r.montant || 0);
      }
    }

    // Ordonnancement : si saison sélectionnée -> ordre Juil → Juin; sinon ordre calendaire croissant
    let ordered = [];
    if (saison) {
      ordered = BUDGET_ORDER.map((mi) => base.find((b) => b.monthIdx === mi));
    } else {
      ordered = base
        .filter((b) => (b.depense + b.recette) > 0)
        .sort((a, b) => a.monthIdx - b.monthIdx);
      if (ordered.length === 0) ordered = base; // fallback
    }

    // Solde cumulée
    let cumul = 0;
    const withSolde = ordered.map((r) => {
      cumul += (r.recette - r.depense);
      return { ...r, solde: Number(cumul.toFixed(2)) };
    });

    // Étiquettes propres
    return withSolde.map((r) => ({
      label: r.month,
      depense: Math.round(r.depense),
      recette: Math.round(r.recette),
      solde: Math.round(r.solde),
    }));
  }, [rows, saison]);

  // Répartitions par catégories
  const { pieDepenses, pieRecettes } = useMemo(() => {
    const depMap = new Map();
    const recMap = new Map();
    for (const r of rows) {
      const cat = r?.categorie || "Non renseigné";
      const m = Number(r?.montant || 0);
      if (!m) continue;
      if (r.type_operation === "Dépense") {
        depMap.set(cat, (depMap.get(cat) || 0) + m);
      } else if (r.type_operation === "Recette") {
        recMap.set(cat, (recMap.get(cat) || 0) + m);
      }
    }
    const dep = Array.from(depMap, ([name, value]) => ({ name, value })).sort(
      (a, b) => b.value - a.value
    );
    const rec = Array.from(recMap, ([name, value]) => ({ name, value })).sort(
      (a, b) => b.value - a.value
    );
    return { pieDepenses: dep, pieRecettes: rec };
  }, [rows]);

  const grid = isDark ? "#334155" : "#e2e8f0";
  const tick = isDark ? "#cbd5e1" : "#334155";
  const cursorFill = isDark ? "rgba(148,163,184,0.12)" : "rgba(148,163,184,0.15)";

  // Palette (dégradés alignés avec ta charte bleu/violet/orange/rose)
  const colors = {
    depTop: isDark ? "#FB7185" : "#FB7185",     // rose-400
    depBottom: isDark ? "#E11D48" : "#F43F5E",  // rose-600/500
    recTop: isDark ? "#34D399" : "#34D399",     // emerald-400
    recBottom: isDark ? "#059669" : "#10B981",  // emerald-600/500
    line: isDark ? "#A78BFA" : "#6366F1",       // violet / indigo
  };
  const PIE_COLORS = [
    "#6366F1", "#8B5CF6", "#EC4899", "#F59E0B", "#10B981",
    "#3B82F6", "#F43F5E", "#22C55E", "#F97316", "#06B6D4",
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const dep = payload.find((p) => p.dataKey === "depense")?.value || 0;
    const rec = payload.find((p) => p.dataKey === "recette")?.value || 0;
    const sol = payload.find((p) => p.dataKey === "solde")?.value ?? null;
    return (
      <div
        className={`rounded-xl border px-3 py-2 shadow-sm ${
          isDark
            ? "bg-slate-900 border-slate-700 text-slate-100"
            : "bg-white border-slate-200 text-slate-800"
        }`}
      >
        <div className="text-[11px] opacity-70 mb-1">{label}</div>
        <div className="text-[12px]">Dépenses : <b>{fmtEUR(dep)}</b></div>
        <div className="text-[12px]">Recettes : <b>{fmtEUR(rec)}</b></div>
        {sol !== null && (
          <div className="text-[12px]">Solde cumulée : <b>{fmtEUR(sol)}</b></div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full space-y-6">
      {/* Chart 1 : Flux mensuels + Solde cumulée */}
      <div className="w-full h-[360px] rounded-xl border border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/40 p-3">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={monthly} margin={{ top: 16, right: 16, left: 8, bottom: 32 }}>
            <defs>
              <linearGradient id="depFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors.depTop} stopOpacity={0.95} />
                <stop offset="100%" stopColor={colors.depBottom} stopOpacity={0.85} />
              </linearGradient>
              <linearGradient id="recFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors.recTop} stopOpacity={0.95} />
                <stop offset="100%" stopColor={colors.recBottom} stopOpacity={0.85} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" stroke={grid} />
            <XAxis
              dataKey="label"
              height={30}
              tick={{ fill: tick, fontSize: 12 }}
            />
            <YAxis
              tick={{ fill: tick, fontSize: 12 }}
              tickFormatter={(v) =>
                Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 0 })
              }
              width={60}
            />
            <Tooltip cursor={{ fill: cursorFill }} content={<CustomTooltip />} />

            <Bar dataKey="depense" fill="url(#depFill)" radius={[8, 8, 0, 0]}>
              <LabelList
                dataKey="depense"
                position="top"
                formatter={(v) =>
                  Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 0 })
                }
                style={{ fill: isDark ? "#e2e8f0" : "#0f172a", fontSize: 11 }}
              />
            </Bar>
            <Bar dataKey="recette" fill="url(#recFill)" radius={[8, 8, 0, 0]}>
              <LabelList
                dataKey="recette"
                position="top"
                formatter={(v) =>
                  Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 0 })
                }
                style={{ fill: isDark ? "#e2e8f0" : "#0f172a", fontSize: 11 }}
              />
            </Bar>
            <Line
              type="monotone"
              dataKey="solde"
              stroke={colors.line}
              strokeWidth={2.5}
              dot={{ r: 2 }}
              activeDot={{ r: 4 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Chart 2 : Répartition par catégories */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="h-[340px] rounded-xl border border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/40 p-3">
          <h3 className="text-sm font-semibold mb-2 text-slate-800 dark:text-slate-100">
            Répartition des <span className="text-rose-600">dépenses</span> par catégorie
          </h3>
          {pieDepenses.length === 0 ? (
            <div className="h-[290px] flex items-center justify-center text-slate-500 dark:text-slate-400 text-sm">
              Aucune dépense
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="90%">
              <PieChart>
                <Tooltip
                  contentStyle={{
                    background: isDark ? "#0f172a" : "#fff",
                    border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                    borderRadius: 12,
                  }}
                  formatter={(v) => fmtEUR(v)}
                />
                <Pie
                  data={pieDepenses}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={110}
                  labelLine={false}
                  label={(e) =>
                    `${e.name} (${(e.percent * 100).toFixed(0)}%)`
                  }
                >
                  {pieDepenses.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="h-[340px] rounded-xl border border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/40 p-3">
          <h3 className="text-sm font-semibold mb-2 text-slate-800 dark:text-slate-100">
            Répartition des <span className="text-emerald-600">recettes</span> par catégorie
          </h3>
          {pieRecettes.length === 0 ? (
            <div className="h-[290px] flex items-center justify-center text-slate-500 dark:text-slate-400 text-sm">
              Aucune recette
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="90%">
              <PieChart>
                <Tooltip
                  contentStyle={{
                    background: isDark ? "#0f172a" : "#fff",
                    border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                    borderRadius: 12,
                  }}
                  formatter={(v) => fmtEUR(v)}
                />
                <Pie
                  data={pieRecettes}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={110}
                  labelLine={false}
                  label={(e) =>
                    `${e.name} (${(e.percent * 100).toFixed(0)}%)`
                  }
                >
                  {pieRecettes.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
